import { z } from 'zod';
import { MachineConfig, MachineConfigErrors, MachineConfigValidation } from '@/types/supply-gpu';

// Zod schema for machine configuration validation
export const machineConfigSchema = z.object({
  gpuModel: z
    .string()
    .min(1, 'GPU model is required')
    .max(100, 'GPU model name is too long'),
  numberOfCards: z
    .number()
    .int('Number of cards must be a whole number')
    .min(1, 'Number of cards must be at least 1')
    .max(8, 'Maximum 8 cards allowed'),
  totalMemory: z
    .number()
    .positive('Total memory must be greater than 0')
    .max(1000, 'Total memory seems unrealistic (max 1000 GB)')
});

// Type inference from Zod schema
export type MachineConfigSchema = z.infer<typeof machineConfigSchema>;

// Validation function using Zod schema
export const validateMachineConfig = (config: MachineConfig): MachineConfigValidation => {
  const result = machineConfigSchema.safeParse(config);
  
  if (result.success) {
    return { isValid: true, errors: {} };
  }

  const errors: MachineConfigErrors = {};
  
  result.error.errors.forEach((error) => {
    const field = error.path[0] as keyof MachineConfigErrors;
    if (field && !errors[field]) {
      errors[field] = error.message;
    }
  });

  return { isValid: false, errors };
};

// Helper function for validating individual fields
export const validateField = (field: keyof MachineConfig, value: any): string | undefined => {
  try {
    const fieldSchema = machineConfigSchema.shape[field];
    fieldSchema.parse(value);
    return undefined;
  } catch (error) {
    if (error instanceof z.ZodError) {
      return error.errors[0]?.message;
    }
    return 'Invalid value';
  }
};

// Helper function for real-time validation
export const validateMachineConfigPartial = (config: Partial<MachineConfig>): Partial<MachineConfigErrors> => {
  const errors: Partial<MachineConfigErrors> = {};
  
  Object.entries(config).forEach(([key, value]) => {
    const field = key as keyof MachineConfig;
    const error = validateField(field, value);
    if (error) {
      errors[field] = error;
    }
  });

  return errors;
};

export const formatMemoryValue = (value: number): string => {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)} TB`;
  }
  return `${value} GB`;
};

export const formatSpeedValue = (value: number): string => {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)} Gbps`;
  }
  return `${value} Mbps`;
};

export const formatTflopsValue = (value: number): string => {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)} PFLOPS`;
  }
  return `${value.toFixed(1)} TFLOPS`;
};

export const getStatusDisplayText = (status: string): string => {
  switch (status) {
    case 'connected':
      return 'Successfully connected';
    case 'pending':
      return 'Connection pending';
    case 'benchmark_failed':
      return 'Benchmark not passed';
    case 'not_connected':
      return 'Not connected';
    default:
      return 'Unknown status';
  }
};

export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'connected':
      return '#10B981';
    case 'pending':
      return '#3B82F6';
    case 'benchmark_failed':
      return '#EF4444';
    case 'not_connected':
      return '#6B7280';
    default:
      return '#6B7280';
  }
};